import { GetServerSideProps } from "next"
import { useContext } from "react"
import { Container, Box } from "@mui/material"

import AppContext from "@secureo/common/context/AppContext/AppContext"
import SEO from "components/SEO"
import TopElement from "containers/IntermediateCategoryPage/TopElement"
import ChildCategoryList from "containers/IntermediateCategoryPage/ChildCategoryList"
import SeoContent from "components/SeoContent"
import BreadCrumbs, { BreadCrumbAncestor, getCountryNameFromLocale } from "components/BreadCrumbs"

import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchIntermediateCategory from "utils/commerceTools/graphql/queries/fetchIntermediateCategory"
import mergeIntermediateCategoryWithProducts from "utils/formatters/mergeIntermediateCategoryWithProducts"
import getValueFromLocalizedStringWithFallback from "utils/i18n/getValueFromLocalizedStringWithFallback"
import {
	getLocalizedMetaTitle,
	getLocalizedMetaDescription,
} from "utils/i18n/getLocalizedMetaAttributeValue"
import { getServerTranslations } from "utils/i18n/getServerTranslations"
import { isSwitzerlandLocale } from "utils/seo/pages/fallback"
import normalizeLocale from "@secureo/common/utils/normalizeLocale"
import serializeJSON from "@secureo/common/utils/serializeJSON"
import { flatFeesByCurrencyCode } from "@secureo/common/config/shippingZonesAndCosts"

import { IntermediateCategoryWithProducts } from "typings/Category"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import getCurrencySymbol from "@secureo/common/utils/forex/getCurrencySymbol"
import { CurrencyCode } from "@secureo/common/typings/Forex"

export const getMetaTitleFallback = (name: string, locale: string, language: string) => {
	switch (language) {
		case "pl":
			return `Kup teraz ${name} w świetnej cenie | SafeHero ${getCountryNameFromLocale(
				locale,
			)}`
		case "de":
		default:
			return `${name} günstig kaufen | SafeHero ${getCountryNameFromLocale(locale)}`
	}
}

export const getMetaDescriptionFallback = (
	name: string,
	locale: string,
	language: string,
	currencyCode: CurrencyCode,
) => {
	switch (language) {
		case "pl":
			return `${name} - zamów teraz bezpośrednio online na SafeHero ${getCountryNameFromLocale(
				locale,
			)}! ✓ Wysyłka gratis od ${
				flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountThresholdGrossCents / 100
			}${getCurrencySymbol(currencyCode)} ✓ Trusted Shop®${
				isSwitzerlandLocale(locale) ? "" : " ✓ Zakup na rachunek"
			}`
		case "de":
		default:
			return `${name} - jetzt direkt online bestellen auf SafeHero ${getCountryNameFromLocale(
				locale,
			)}! ✓ Portofrei ab ${
				flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountThresholdGrossCents / 100
			}${getCurrencySymbol(currencyCode)} ✓ Trusted Shop®${
				isSwitzerlandLocale(locale) ? "" : " ✓ Kauf auf Rechnung"
			}`
	}
}

const IntermediateCategoryPage = ({ category, locale, language, countryCode }: Props) => {
	const { currencyCode } = useContext(AppContext)
	const { childCategories, nameAllLocales, slug } = category
	const sortedChildCategories = childCategories
		.sort((a, b) => {
			return a.orderHint - b.orderHint
		})
		.filter((category) => !category.excludedCountryCodes.includes(countryCode))

	const name = getValueFromLocalizedStringWithFallback(nameAllLocales, locale)

	const metaTitle =
		getLocalizedMetaTitle(category, locale) || getMetaTitleFallback(name, locale, language)
	const metaDescription =
		getLocalizedMetaDescription(category, locale) ||
		getMetaDescriptionFallback(name, locale, language, currencyCode)

	const breadCrumb: BreadCrumbAncestor = {
		label: name,
		href: `/pc/${slug}`,
	}

	return (
		<>
			<SEO
				asPath={`/pc/${slug}`}
				title={metaTitle}
				pageType={"category"}
				description={metaDescription}
			/>
			<Box mb={5}>
				<BreadCrumbs currentPath={breadCrumb} />
				<Box pb={5} style={{ background: "white" }}>
					<TopElement category={category} locale={locale} />
					<Box mt={5}>
						<Container>
							{sortedChildCategories.map((category) => {
								const { id, slug } = category
								return (
									<ChildCategoryList
										category={category}
										slug={slug}
										key={id}
										locale={locale}
										countryCode={countryCode}
									/>
								)
							})}
						</Container>
					</Box>
				</Box>
				<Box mt={5}>
					<Container>
						<SeoContent category={category} language={language} />
					</Container>
				</Box>
			</Box>
		</>
	)
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { res, params, locale: rawLocale } = context

	const slug = params?.slug as string
	const { locale, language, countryCode } = normalizeLocale(rawLocale)

	const accessToken = await anonymousAccessTokenCache()

	// Fetch category and child categories
	const category = await fetchIntermediateCategory(accessToken, language, slug)
	if (!category) {
		res.statusCode = 404
		return {
			notFound: true,
			props: {
				statusCode: 404,
				error: "Category not found",
			},
		}
	}

	// Merge categories and products
	const categoryWithProducts = mergeIntermediateCategoryWithProducts(category)

	categoryWithProducts.showInNavbar = category.custom.customFieldsRaw?.find(
		({ name }) => name === "showInNavbar",
	)?.value

	categoryWithProducts.shortSeoText = category.custom.customFieldsRaw?.find(
		({ name }) => name === "shortSeoText",
	)?.value

	categoryWithProducts.longSeoText = category.custom.customFieldsRaw?.find(
		({ name }) => name === "longSeoText",
	)?.value

	const props: Props = {
		category: categoryWithProducts,
		locale,
		language,
		countryCode,
		...(await getServerTranslations(locale, ["common", "countries", "languages"])),
	}

	return {
		props: serializeJSON(props),
	}
}

interface Props {
	locale: string
	language: string
	category: IntermediateCategoryWithProducts
	countryCode: CountryCode
}

export default IntermediateCategoryPage
